import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { RequestService } from 'app/core/services/request.service';
import { ConfigService } from 'app/core/services/config.service';
import { StorageService } from 'app/core/services/storage.service';
import { DateUtils } from '../../../core/components/common/date-utils';
import { ProductService } from './product.service';
import { Subject } from 'rxjs/Subject';
import { Field } from '../field';

declare var moment: any;

@Injectable()
export class ClassesService {

    private cache$: Observable<any>;
    private reload$ = new Subject<void>();

    private brandedPortalUrl: Observable<any>;
    constructor(
        private requestService: RequestService,
        private configService: ConfigService,
        private storageService: StorageService,
        private productService: ProductService
    ) {
    }

    getCategories(): Observable<any> {
        if (!this.cache$) {
            this.cache$ = this.requestCategories().takeUntil(this.reload$).shareReplay(1);
        }
        return this.cache$;
    }

    private requestCategories() {
        return this.productService.getProductGroups(1, { allProducts: true }).map(body => body.data);
    }

    refreshCategories(): any {
        // Calling next will complete the current cache instance
        this.reload$.next();

        // Setting the cache to null will create a new cache the
        // next time 'jokes' is called
        this.cache$ = null;
    }

    setCustomLearnerMessage(classId: string, data: any): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.classevent.email.customMessage.learner.api + '/' + classId;
        return this.requestService.post(url, data);
    }

    setCustomInstructorMessage(classId: string, data: any): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.classevent.email.customMessage.instructor.api + '/' + classId;
        return this.requestService.post(url, data);
    }

    getBrandedPortalUrl(): Observable<any> {
        if (!this.brandedPortalUrl) {
            let url = this.configService.get('server').url + this.configService.get('endpoints').solar.user.brandedPortalUrl.api;
            this.brandedPortalUrl = this.requestService.get(url)
                .map(_ => _.data)
                .publishReplay()
                .refCount();
        }

        return this.brandedPortalUrl;
    }

    getSignatories(): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').agora.employees.signatoryListJWT.api;
        return this.requestService.get(url);
    }

    getDomain(): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').agora.accounts.domain.api;
        return this.requestService.get(url);
    }

    getTimezonesDictionary(): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.timezone.dictionary.api;
        return this.requestService.get(url);
    }

    patchExam(classId: string, data: any): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.classevent.exam.patch.api + '/' + classId;
        return this.requestService.post(url, [data]);
    }

    addClass(data: any): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.classevent.add.api;
        return this.requestService.post(url, data);
    }

    editClass(data: any): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.classevent.edit.api;
        return this.requestService.post(url, data);
    }

    deleteClass(classId: string): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.classevent.delete.api;
        return this.requestService.post(url, { id: classId });
    }

    releaseClass(classId: string): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.classevent.release.api + '/' + classId;
        return this.requestService.post(url, {});
    }

    getClass(id: string): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.classevent.details.api + '/' + id;
        return this.requestService.get(url);
    }

    getAddresses(): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.classevent.address.dictionary.api;
        return this.requestService.get(url, {page: 1, limit: 100000});
    }

    addLearners(classId: string, emails: string[]): Observable<any> {
        let url = this.configService.get('server').url +
            this.configService.get('endpoints').solar.classevent.learner.patch.add.api + '/' + classId;
        return this.requestService.post(url, emails);
    }

    editLearner(classId: string, learner: any): Observable<any> {
        let url = this.configService.get('server').url +
            this.configService.get('endpoints').solar.classevent.learner.patch.edit.api + '/' + classId;
        return this.requestService.post(url, learner);
    }

    removeLearner(classId: string, email: string): Observable<any> {
        let url = this.configService.get('server').url +
            this.configService.get('endpoints').solar.classevent.learner.patch.remove.api + '/' + classId;
        return this.requestService.post(url, [email]);
    }

    getAccreditedInstructors(productGroupId: string): Observable<any> {
        let url = this.configService.get('server').url +
            this.configService.get('endpoints').solar.instructor.accredited.api + '?productGroupId=' + productGroupId;
        return this.requestService.get(url);
    }

    listInstructors(): Observable<any> {
        let url = this.configService.get('server').url +
            this.configService.get('endpoints').solar.courseEvent.instructor.list.api;
        return this.requestService.get(url);
    }

    getInstructor(email: string, suppressError: boolean = false): Observable<any> {
        let url = this.configService.get('server').url +
            this.configService.get('endpoints').solar.instructor.email.api + '/' + email;
        return this.requestService.get(url, null, null, suppressError);
    }

    promoteToInstructor(id: string): Observable<any> {
        let url = this.configService.get('server').url +
            this.configService.get('endpoints').solar.instructor.promote.api + '/' + id;
        return this.requestService.post(url, {});
    }

    addInstructor(data: any): Observable<any> {
        let url = this.configService.get('server').url +
            this.configService.get('endpoints').solar.instructor.add.api;
        return this.requestService.post(url, data);
    }

    updateInstructors(classId: string, changedGroups: any[]): Observable<any> {
        let url = this.configService.get('server').url +
            this.configService.get('endpoints').solar.classevent.instructor.patch.api + '/' + classId;
        return this.requestService.post(url, changedGroups);
    }

    editInstructor(user: any) {
        let url = this.configService.get('server').url +
            this.configService.get('endpoints').solar.user.edit.api;
        return this.requestService.post(url, user);
    }

    toggleInstructor(user: any, choice?: any) {

        let url = this.configService.get('server').url +
            this.configService.get('endpoints').solar.instructor.toggleUser.api + (choice ? '?whitelisted=' + choice : '');
        return this.requestService.post(url, user);
    }

    getLearners(page: number, filter?: string): Observable<any> {
        // let url = this.configService.get('server').url +
        //     this.configService.get('endpoints').solar.instructor.accredited.api
        // return this.requestService.get(url);
        return Observable.of({
            data: [
                { email: 'pesho', vouchers: [] },
                { email: 'gosho', vouchers: [] },
                { email: 'mitio pedala', vouchers: [] }
            ], flags: { page: page, count: 30, size: 10 }
        });
    }

    updateVariants(classId: string, variants: string[]): Observable<any> {
        let url = this.configService.get('server').url +
            this.configService.get('endpoints').solar.classevent.products.patch.api + '/' + classId;
        return this.requestService.post(url, variants);
    }

    addVoucher(classId: string, learnerId: string, voucherNumber: string): Observable<any> {
        //assignmentType: 'CLASS' --> assignment created in a class
        let data = [{
            userId: learnerId,
            voucherNumbers: [voucherNumber],
            assignmentType: 'CLASS'
        }];

        let url = this.configService.get('server').url +
            this.configService.get('endpoints').solar.classevent.assignment.assign.api + '/' + classId;
        return this.requestService.post(url, data);
    }

    addVouchers(classId: string, temporaryVouchers: any): Observable<any> {
        let data = []
        //assignmentType: 'CLASS' --> assignment created in a class
        for (let tempVoucher of temporaryVouchers) {
            data.push({ userId: tempVoucher.learner.id, voucherNumbers: tempVoucher.vouchers.map(v => v.voucherNumber), assignmentType: 'CLASS' })
        }

        let url = this.configService.get('server').url +
            this.configService.get('endpoints').solar.classevent.assignment.assign.api + '/' + classId;
        return this.requestService.post(url, data);
    }

    removeVoucher(classId: string, assignmentId: string): Observable<any> {
        let url = this.configService.get('server').url +
            this.configService.get('endpoints').solar.classevent.assignment.unassign.api + '/' + classId;
        return this.requestService.post(url, [assignmentId]);
    }

    removeVouchers(classId: string, assignmentIds: any): Observable<any> {
        let url = this.configService.get('server').url +
            this.configService.get('endpoints').solar.classevent.assignment.unassign.api + '/' + classId;
        return this.requestService.post(url, assignmentIds);
    }

    getLearnersVouchers(classId: string, page: number, learnerFilter: string = '', limit: number = 10): Observable<any> {
        let query = `?page=${page}&limit=${limit}`;
        if (learnerFilter) {
            query += `&filter=${learnerFilter}`;
        }

        let url = this.configService.get('server').url +
            this.configService.get('endpoints').solar.classevent.assignment.list.api + '/' + classId + query;

        return this.requestService.get(url);
    }

    getVouchers(classId: string, page: number, productVariantId: string, filters: any[], filter: string = '',
    sortFields: any[] = null, temporaryVouchers: any = null): Observable<any> {
        let query = filters
            .reduce((queryStr, filter) => `${queryStr}&@${filter.key}=${filter.value}`, '?');
        let sort = sortFields.length > 0 ? sortFields
            .reduce((queryStr, filter) => `${queryStr}&${filter.field}=${filter.type}`, '') : '';

        query = `${query}&page=${page}&limit=10&filter=${filter}${sort}`;
        if (productVariantId) {
            query = `${query}&productVariantId=${productVariantId}`;
        }
        if (temporaryVouchers && temporaryVouchers.length > 0) {
            query = `${query}&!voucherNumber=${temporaryVouchers.filter(l => l.vouchers.length > 0).map(v => v.vouchers.map(v => v.voucherNumber))}`;
        }

        let url = this.configService.get('server').url +
            this.configService.get('endpoints').solar.classevent.voucher.list.api + '/' + classId + query;

        return this.requestService.get(url);
        // return Observable.of({
        //     data: [
        //         { id: '23DSF213SA', name: 'Courseware', expireDate: new Date(), status: 'new' },
        //         { id: '325HHKEF32', name: 'Exams', expireDate: new Date(), status: 'new' }
        //     ],
        //     flags: { page: page, count: 30, size: 10 }
        // }).delay(1000);
    }

    getTemplatesByType(classId: string, typeId: string): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.classevent.email.template.byType.api;
        return this.requestService.get(url, { classId: classId, typeId: typeId });
    }

    deleteTemplate(classId: string, typeId: string, templateId: string, message: string, globalForType?: boolean): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.classevent.email.template.delete.api;
        return globalForType ? this.requestService.post(url, { classId: classId, typeId: typeId, templateId: templateId, customMessage: message, globalForType: globalForType }) :
            this.requestService.post(url, { classId: classId, typeId: typeId, templateId: templateId, customMessage: message });
    }

    selectTemplate(classId: string, typeId: string, templateId: string, message: string, globalForType?: boolean): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.classevent.email.template.select.api;
        return globalForType ? this.requestService.post(url, { classId: classId, typeId: typeId, templateId: templateId, customMessage: message, globalForType: globalForType }) :
            this.requestService.post(url, { classId: classId, typeId: typeId, templateId: templateId, customMessage: message });
    }

    selectGlobalTemplate(typeId: string, templateId: string): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.classevent.email.template.global.api;
        return this.requestService.post(url, { typeId: typeId, templateId: templateId });
    }

    getSelectedTemplate(classId: string, typeId: string, globalForType?: boolean): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.classevent.email.template.selected.api;
        return globalForType ? this.requestService.get(url, { classId: classId, typeId: typeId, globalForType: globalForType }) :
            this.requestService.get(url, { classId: classId, typeId: typeId });
    }

    getTemplateForPreview(classId: string, template: string, type: string): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.classevent.email.preview.api + '/' + classId;
        return this.requestService.get(url, { template: template, type: type });
    }

    getCustomTemplatePreview(template: string, customMessage: string): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.classevent.email.previewCustomTemplate.api;
        return this.requestService.get(url, { template: template, customMessage: customMessage });
    }

    getTemplates(type: string): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').tm.emailTemplate.list.api;
        return this.requestService.get(url, { isChunked: true, typeId: type });
    }

    getCertificateTemplatesList(): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.classevent.certificate.listTemplates.api;
        return this.requestService.get(url);
    }

    saveCustomMessageCertificateEmail(classId: string, message: any): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.classevent.email.customMessage.certificate.api + '/' + classId;
        return this.requestService.post(url, message);
    }

    sendCertificateEmail(data: any): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.classevent.certificate.send.api;
        return this.requestService.post(url, data)
    }
    getCertificateTemplatePreview(template: any): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.classevent.certificate.preview.api;
        return this.requestService.post(url, template);
    }

    previewCertificate(data: any): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.classevent.certificate.preview.api;
        return this.requestService.post(url, data);
    }

    downloadCertificate(data: any): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.classevent.certificate.download.api;
        return this.requestService.post(url, data);
    }

    learnerCertificateScheduleEditBulk(learners: any[]): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.classevent.certificate.learner.schedule.api;
        return this.requestService.post(url, learners);
    }

    sendCertificates(classId: string, learners: Array<{ username: string }>): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.classevent.certificate.send.api;
        return this.requestService.post(url, { classEventId: classId, learners: learners });
    }

    getEmailTemplateSendCertificatePreview(template: any): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.classevent.certificate.emailPreview.api;
        return this.requestService.post(url, template);
    }

    getBuyerDetails(): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').organization.organizationDetails.details.api;
        return this.requestService.get(url);
    }

    getBrandedUrl(): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.domain.brandedUrl.api;
        return this.requestService.get(url);
    }

    getLanguages(): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.language.dictionary.api;
        return this.requestService.get(url);
    }

    putUserPreference(userId: string, data: any): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').amber.users.putPreference.api + userId;
        return this.requestService.post(url, data);
    }

    getUserPreference(userId: string, key: string): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').amber.users.getPreference.api + userId + '?key=' + key;
        return this.requestService.get(url);
    }

    getProducts(productGroupId: string, skus: any) {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.product.availableByGroup.api + '?productGroupId=' +
            productGroupId +
            '&@sku=' + skus + '&allProducts=true';
        return this.requestService.get(url);
    }

    createBetaClass(classEvent: any): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.classevent.newWorkFlow.add.api;
        return this.requestService.post(url, classEvent);
    }

    createDraft(draftData: any): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.classevent.newWorkFlow.drafts.create.api;
        return this.requestService.post(url, draftData);
    }

    loadDraft(id: any): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.classevent.newWorkFlow.drafts.load.api + '/' + id;
        return this.requestService.get(url);
    }

    getDrafts(): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.classevent.newWorkFlow.drafts.list.api;
        return this.requestService.get(url);
    }

    bulkDeleteDrafts(draftData: any): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.classevent.newWorkFlow.drafts.bulkDelete.api;
        return this.requestService.post(url, draftData);
    }


    getClassAddFormFields(): any[] {
        return [
            {
                key: 'name',
                label: 'Class Name:',
                type: 'textbox',
                placeholder: 'Class-X',
                required: true
            }, {
                key: 'startDate',
                label: 'Course Start Date:',
                type: 'datetimepicker',
                required: false
            }, {
                key: 'endDate',
                label: 'Course End Date:',
                type: 'datetimepicker',
                required: false
            }, {
                key: 'location',
                label: 'Location:',
                type: 'textbox',
                placeholder: 'Enter address',
                required: false
            }
        ];
    }

    getClassGridFields(): any[] {
        return [
            {
                title: 'Session Type',
                name: 'type.configKey',
                sortable: true,
                config: {
                    type: 'template',
                    template: 'typeTemplate',
                }
            },
            {
                title: 'Display Name',
                name: 'displayName',
                config: {
                    type: 'link',
                    route: (data, item) => this.path(data, item),
                    key: 'id'
                },
                sortable: true
                // formatter: (data, item) => this.hideText(data, item)
            },
            {
                id: 'start',
                title: 'Start Date',
                name: 'startDate',
                formatter: (date, item) => this.formatDate(date, item),
                sortable: true
            },
            {
                title: 'End Date',
                name: 'endDate',
                formatter: (date, item) => this.formatDate(date, item),
                sortable: true
            },
            {
                title: 'Venue',
                name: 'venue.name',
                sortable: true
            },
            {
                title: 'Updated',
                name: 'updatedAt',
                formatter: (date, item) => this.formatDateTime(date, item),
                sortable: true
            },
            {
                title: 'Status',
                name: 'status.configValue',
                sortable: true
            }
        ];
    }
    getDraftClassGridFields(): any[] {
        return [
            {
                title: 'Session Type',
                name: 'type',
                formatter: (data, item) => {
                    if (data === 'exam')
                        return 'Session Exam'
                    return 'Class'
                },
                sortable: true
            },
            {
                title: 'Session Name',
                name: 'classEventDTO.displayName',
                config: {
                    type: 'link',
                    route: (data, item) => `/classes/beta-create/${data}`,
                    key: 'id'
                },
                sortable: true
            },
            {
                title: 'Initiated By',
                name: 'createdBy',
                sortable: true
            },
            {
                title: 'Initiated at',
                name: 'createdAt',
                formatter: (date, item) => this.formatDateTime(date, item),
                sortable: true
            },
            {
                title: 'Updated at',
                name: 'updatedAt',
                formatter: (date, item) => this.formatDateTime(date, item),
                sortable: true
            }
        ];
    }

    getClassInfoFields(): any[] {
        return [
            {
                key: 'name',
                label: 'Name',
                type: 'text'
            },
            {
                key: 'startDate',
                label: 'Start Date',
                type: 'date'
            },
            {
                key: 'endDate',
                label: 'End Date',
                type: 'date'
            },
            // {
            //     key: 'venue',
            //     value: 'N/A',
            //     label: 'Location',
            //     type: 'text'
            // },
            {
                key: 'releaseDate',
                label: 'Release Date',
                type: 'date'
            },
            {
                key: 'venue.name',
                label: 'Location',
                type: 'text',
                placeholder: 'N/A'
            },
            {
                key: 'status.configValue',
                label: 'Status',
                type: 'text',
                placeholder: 'N/A'
            }
        ];
    }

    getbetaClassInfoFields(): any[] {
        return [
            {
                name: 'name',
                title: 'Name',
                sortable: true
            }, {
                name: 'deliveryFormat',
                title: 'Delivery Format',
                sortable: true
            }
            , {
                name: 'certificateType',
                title: 'Certificate Type',
                sortable: true
            }, {
                name: 'languageValue',
                title: 'Language',
                sortable: true
            },
            {
                name: 'voucherSummary.new',
                title: 'Vouchers',
                config: {
                    type: 'template',
                    template: 'voucherTemplate',
                },
                sortable: true,
            }
        ];
    }

    populateBetaClassFields(): any[] {
        return [new Field({
            title: 'Vouchers',
            id: 'vouchers',
            updateEditFields: (a, b) => b.options = [{
                key: 'noVoucher',
                value: 'Show products with no vouchers',
                checked: true
            }]
        }), new Field({
            title: 'Delivery Formats',
            id: 'deliveryFormat',
        }), new Field({
            title: 'Certificate Types',
            id: 'certificateType',
        }), new Field({
            title: 'Languages',
            id: 'languageValue',
        })];
    }

    getClassInfoButtons(released: boolean, canSendInvitations: boolean): any[] {
        return [
            {
                title: released ? 'Invitations are LIVE' : 'Send Invitations Now',
                action: 'release',
                icon: 'fa-paper-plane',
                class: 'btn-warning',
                disabled: released || !canSendInvitations
            },
            {
                title: 'Edit',
                action: 'edit',
                icon: 'fa-pencil-square',
                class: 'btn-primary'
            },
            {
                title: 'Delete',
                action: 'delete',
                icon: 'fa-times',
                class: 'btn-danger'
            }
        ];
    }

    private formatDate(date: any, item: any) {
        if ('examAssignmentComplete' === item.type.configKey
            && 'Complete' === item.productVariants[0].examFulfillmentType) {
            let exam = item.exams;
            return exam[Object.keys(exam)[0]].date ? moment(new Date(exam[Object.keys(exam)[0]].date)).format('D MMM YYYY') : 'N/A';
        }
        return date ? moment(new Date(date)).format('D MMM YYYY') : 'N/A';
    }

    private formatDateTime(date: any, item: any) {
        let localDate;
        if (item.timezone)
            localDate = moment(moment(date).utc().add(moment.tz(item.timeZone)).format());
        else
            localDate = moment(moment(date).utc().format());
        return moment(localDate).format('D MMM YYYY') + ' at ' + moment(localDate).format('HH:mm');
    }

    private hideText(data: string, item: any): string {
        if ('examAssignmentComplete' === item.type.configKey
            && 'Complete' === item.productVariants[0].examFulfillmentType)
            return '';
        return data;
    }

    isModalFirstTime(modalKey: string): boolean {

        let history = this.storageService.get('openedModals');
        history = history || {};
        history['classes'] = history['classes'] || [];

        return !(history['classes'] && history['classes'].some(i => i == modalKey))
    }

    markModalAsOpened(modalKey: string) {
        let history = this.storageService.get('openedModals');
        history = history || {};
        history['classes'] = history['classes'] || [];

        if (!history['classes'].some(i => i == modalKey)) {
            history['classes'].push(modalKey);
            this.storageService.set('openedModals', history);
        }
    }

    private path(data: any, item: any) {
        return 'examAssignmentComplete' === item.type.configKey
            ? `/classes/edit-exam/${data}` : `/classes/edit/${data}`
    }

    testSmtp(details: any): Observable<any> {
        let url =
            this.configService.get('server').url +
            this.configService.get('endpoints').agora.accounts.testAgnosticSmtp.api;
        return this.requestService.post(url, details, true);
    }
}



// WEBPACK FOOTER //
// ./src/app/solar/common/services/classes.service.ts